import "./auth.css";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import Modal from "react-bootstrap/Modal";
import CheckEmailModal from "./CheckEmailModal";
import Toaster from "../../components/Toast/Toast";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ForgotPassword } from "../../api/modules/b2droppie.class";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";

const ForgetPassword = (props) => {
  const dispatch = useDispatch();
  const initialValues = { email: "" };
  const [modalShow, setModalShow] = useState(false);
  const [error, setError] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 641);

  // Swipe to close states
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const cardRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 641);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset drag when modal opens
  useEffect(() => {
    if (props.show) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [props.show]);

  // Swipe handlers
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    // Only allow dragging down
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);

    // If dragged more than 150px, close the modal
    if (dragY > 150) {
      props.onHide();
    }
    setDragY(0);
  };

  const ValidationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
  });

  const handleSubmit = async (values) => {
    props.setLoading(true);
    try {
      const response = await ForgotPassword(values);
      setError({
        success: response?.data?.message || "",
        error: response?.data?.error?.message || "",
      });
      props.onHide();
      if (response?.data?.success) {
        props?.onHide();
        setModalShow(true);
      }
    } catch (err) {
      console.error(err);
      setError({ success: "", error: "Something went wrong. Try again." });
    } finally {
      props.setLoading(false);
    }
  };

  const handleBack = () => {
    props?.onHide();
    dispatch(toggleOnboard({ login: true }));
  };

  const backdropOpacity = isMobile ? Math.max(0, 1 - dragY / 300) : 1;

  return (
    <>
      <Toaster message={error} />
      <Modal
        centered={!isMobile}
        size="md"
        {...props}
        className={`modern-auth-modal ${isMobile ? "mobile-bottom-sheet" : ""}`}
        dialogClassName="modern-modal-dialog"
        backdrop={true}
        animation={false}
        style={isMobile ? { "--backdrop-opacity": backdropOpacity } : {}}
      >
        <Modal.Body className="modern-modal-body m-0 p-0">
          <div
            ref={cardRef}
            className={`modern-auth-card ${isDarkMode ? "dark-mode dark" : "light-mode light"}`}
            style={
              isMobile
                ? {
                    transform: `translateY(${dragY}px)`,
                    transition: isDragging ? "none" : "transform 0.3s ease-out",
                  }
                : {}
            }
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe Handle - Only on mobile */}
            {isMobile && (
              <div
                ref={(el) => {
                  if (el)
                    el.style.setProperty("background", "#1A1A1A", "important");
                }}
                className="swipe-handle-container"
              >
                <div className="swipe-handle" />
              </div>
            )}
            <Formik
              onSubmit={handleSubmit}
              initialValues={initialValues}
              validationSchema={ValidationSchema}
            >
              {({ values, errors, touched, handleChange, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <h2
                    className={`text-center text-24px text-fw-400 text-AktivGrotesk-Bold mt-2 mb-0 ${
                      isDarkMode ? "text-white" : "text-deep-purple"
                    }`}
                  >
                    Forgot Password?
                  </h2>
                  <p
                    className={`text-center text-16px text-fw-400 text-AktivGrotesk-Regular mb-1 mt-3 ${
                      isDarkMode ? "text-light-gray" : "text-charcoal-gray"
                    }`}
                  >
                    Enter your email below and we'll send you a link to reset
                    your password.
                  </p>

                  <div className="loginElement">
                    <label className="modern-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      isDarkMode={isDarkMode}
                      data-value={values.email}
                      className="w-100 modern-input"
                      placeholder="e.g. john.smith@example.com"
                    />
                    {errors.email && touched.email && (
                      <span className="error small text-danger">
                        {errors.email}
                      </span>
                    )}
                  </div>

                  <div className="d-flex gap-3 justify-content-center mt-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      className={`w-50 ${
                        isDarkMode
                          ? "deep-primary-monochrome-btn-dark"
                          : "outlined-medium-deep-purple-btn-light"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`w-50 ${
                        isDarkMode
                          ? "filled-medium-deep-yellow-btn-dark"
                          : "filled-medium-deep-purple-btn-light"
                      }`}
                    >
                      <span>Send Reset Link</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
            {isMobile && (
              <div
                ref={(el) => {
                  if (el)
                    el.style.setProperty("background", "#1A1A1A", "important");
                }}
                className="swipe-handle-container-bottom"
              >
                <div className="swipe-handle-bottom" />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <CheckEmailModal show={modalShow} onHide={() => setModalShow(false)} />
    </>
  );
};

export default ForgetPassword;
